import axios, { AxiosError, AxiosInstance } from 'axios';
import { toast } from 'sonner';
import apiClient from '../apiClient';

export interface IssuePairingRequest {
  desktopClientId: string;
  sessionId: string;
  patientId?: string;
  recordingSessionId?: string;
}

export interface IssuePairingResponse {
  code: string;
  expiresAt: number;
}

export interface RedeemPairingResponse {
  phoneToken: string;
  sessionId: string;
  patientId?: string;
  recordingSessionId?: string;
  expiresAt: number;
}

const ENVELOPE_FALLBACK_KEYS = ['data', 'payload', 'result'] as const;

function unwrap<T>(raw: unknown): T {
  if (raw && typeof raw === 'object') {
    for (const key of ENVELOPE_FALLBACK_KEYS) {
      const v = (raw as Record<string, unknown>)[key];
      if (v && typeof v === 'object') return v as T;
    }
  }
  return raw as T;
}

/**
 * Auth-free axios instance for the redeem endpoint. The mobile capture
 * page is intentionally a no-login surface — it must not present any
 * Bearer token (a stale one in localStorage would be sent by the shared
 * apiClient and could trigger the global 401-redirect-to-login on any
 * downstream error). This instance has no request/response interceptors.
 *
 * Issue is still done via apiClient because it's a doctor-authenticated
 * action triggered from the desktop modal.
 */
let publicClient: AxiosInstance | null = null;
function getPublicClient(): AxiosInstance {
  if (publicClient) return publicClient;
  publicClient = axios.create({
    baseURL: apiClient.defaults.baseURL,
    timeout: 10000,
  });
  // Mirror the shared apiClient's 429 toast so the public mobile-redeem
  // surface still tells the user to slow down. We deliberately stop
  // short of the 401-refresh path — this client must stay auth-free.
  publicClient.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      if (error.response?.status === 429) {
        const headers = error.response.headers as
          | Record<string, string | undefined>
          | undefined;
        const retryAfter = Number(
          headers?.['retry-after'] ?? headers?.['Retry-After'],
        );
        toast.warning('You are sending too many requests', {
          id: 'api-rate-limit',
          description:
            Number.isFinite(retryAfter) && retryAfter > 0
              ? `Try again in ${Math.ceil(retryAfter)}s.`
              : 'Try again in a moment.',
          duration: 6000,
        });
      }
      return Promise.reject(error);
    },
  );
  return publicClient;
}

export const scribePairingApi = {
  async issue(req: IssuePairingRequest): Promise<IssuePairingResponse> {
    const res = await apiClient.post('/scribe-pairing/issue', req);
    return unwrap<IssuePairingResponse>(res.data);
  },

  async redeem(code: string): Promise<RedeemPairingResponse> {
    const res = await getPublicClient().post('/scribe-pairing/redeem', { code });
    return unwrap<RedeemPairingResponse>(res.data);
  },
};
